import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CommunicationTableData } from './types';
import { getCommunicationService } from './communicationService';
import { waitForCompanyID } from '@/js/external-js/variableModules/utils/waitForCompanyID.util';

export const COMMUNICATION_QUERY_KEY = 'communicationData';

export const useCommunicationData = () => {
  const communicationService = getCommunicationService();
  
  return useQuery<CommunicationTableData, Error>({
    queryKey: [COMMUNICATION_QUERY_KEY],
    queryFn: async () => {
      const companyId = await waitForCompanyID();
      return communicationService.fetchCommunicationData(companyId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useUpdateCommunicationData = () => {
  const queryClient = useQueryClient();
  const communicationService = getCommunicationService();
  
  return useMutation<{
    success: boolean;
  }, Error, Partial<CommunicationTableData>>({
    mutationFn: async (data) => {
      const companyId = await waitForCompanyID();
      await communicationService.updateCommunicationData(companyId, data);
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [COMMUNICATION_QUERY_KEY] });
    },
  });
};

export const useAddNewFrequency = () => {
  const queryClient = useQueryClient();
  const communicationService = getCommunicationService();
  
  return useMutation<{
    success: boolean;
  }, Error, string>({
    mutationFn: async (frequency) => {
      await communicationService.addNewFrequency(frequency);
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [COMMUNICATION_QUERY_KEY] });
    },
  });
};

export const useAddNewCommunicationChannel = () => {
  const queryClient = useQueryClient();
  const communicationService = getCommunicationService();
  
  return useMutation<{
    success: boolean;
  }, Error, string>({
    mutationFn: async (channel) => {
      await communicationService.addNewCommunicationChannel(channel);
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [COMMUNICATION_QUERY_KEY] });
    },
  });
};
